<template>
  <Dropdown
    ref="dropdown"
    class="price-filter-dropdown dropdown--multi"
    caption="Preis"
    :disabled="disabled"
    @toggle="onToggle"
    @clickaway="onClickaway"
  >
    <ul class="price-filter-dropdown__items">
      <DropdownItem
        :selected="discountIndeterminate"
        @click="internalDiscounted = undefined"
      >
        Alle Angebote
      </DropdownItem>
      <DropdownItem
        v-if="allowDiscounts"
        :selected="internalDiscounted === true"
        :count="discountedCount"
        :disabled="discountedCount === 0"
        @click="internalDiscounted = true"
      >
        Nur rabattierte Angebote
      </DropdownItem>

      <!-- Price -->
      <li
        v-if="!$config.public.performanceMode.enabled"
        class="price-filter-dropdown__input-item"
      >
        <TextInput
          :id="fromId"
          v-model="minPrice"
          placeholder="0"
          min="0"
          max="100000"
          label="von"
          step="1"
          @keyup.enter="change"
        />
        <div class="price-filter-dropdown__input-appendix">€</div>
      </li>
      <li
        v-if="!$config.public.performanceMode.enabled"
        class="price-filter-dropdown__input-item"
      >
        <TextInput
          :id="toId"
          v-model="maxPrice"
          type="number"
          placeholder="0"
          min="0"
          max="100000"
          label="bis"
          step="1"
          @keyup.enter="change"
        />
        <div class="price-filter-dropdown__input-appendix">€</div>
      </li>
    </ul>

    <template #submit>
      <LinkButton
        tag="button"
        type="button"
        :disabled="!hasChanged"
        primary
        @click="change"
      >
        Übernehmen
      </LinkButton>
    </template>
  </Dropdown>
</template>

<script>
import { isEqual, isNull, isUndefined } from 'lodash-es';
import { TextInput, LinkButton } from '@i22-td-smarthome/component-library';
import Dropdown from '@/components/dropdown.vue';
import DropdownItem from '@/components/dropdown-item.vue';

export default {
  name: 'PriceFilterDropdown',
  components: {
    TextInput,
    Dropdown,
    DropdownItem,
    LinkButton,
  },
  props: {
    discountedCount: {
      type: Number,
      default: null,
    },
    discounted: {
      type: Boolean,
      default: undefined,
    },
    onetimePrice: {
      type: Object,
      default: null,
    },
    disabled: {
      type: Boolean,
    },
    allowDiscounts: {
      type: Boolean,
      default: true,
    },
    id: {
      type: String,
      default: '',
    },
  },
  emits: ['change'],
  data() {
    return {
      minPrice: undefined,
      maxPrice: undefined,
      internalDiscounted: this.discounted,
    };
  },
  computed: {
    discountIndeterminate() {
      return isNull(this.internalDiscounted) || isUndefined(this.internalDiscounted);
    },
    cleanMinPrices() {
      const min = Number.parseInt(this.minPrice, 10) || undefined;
      const max = Number.parseInt(this.maxPrice, 10) || undefined;
      if (min > 0 && max > 0) {
        return {
          min: Math.min(min, max),
          max: Math.max(min, max),
        };
      }
      if (min > 0) {
        return { min };
      }
      if (max > 0) {
        return { max };
      }

      return undefined;
    },
    originalValue() {
      const onetimePrice = { min: undefined, max: undefined };
      if (this.onetimePrice) {
        Object.assign(onetimePrice, this.onetimePrice);
      }
      return {
        discounted: this.discounted,
        onetimePrice,
      };
    },
    changedValue() {
      return {
        discounted: this.internalDiscounted,
        onetimePrice: this.cleanMinPrices,
      };
    },
    hasChanged() {
      return !isEqual(this.originalValue, this.changedValue);
    },
    fromId() {
      return this.id ? `pricesFrom-${this.id}` : 'pricesFrom';
    },
    toId() {
      return this.id ? `pricesTo-${this.id}` : 'pricesTo';
    },
  },
  watch: {
    discounted(discounted) {
      this.internalDiscounted = discounted;
    },
    onetimePrice: {
      handler(onetimePrice) {
        if (onetimePrice) {
          this.minPrice = onetimePrice.min?.toString();
          this.maxPrice = onetimePrice.max?.toString();
        } else {
          this.minPrice = undefined;
          this.maxPrice = undefined;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    close() {
      this.$refs.dropdown.close();
    },
    change() {
      this.$emit('change', this.changedValue);
      this.close();
    },
    reset() {
      if (this.onetimePrice) {
        this.minPrice = this.onetimePrice.min?.toString();
        this.maxPrice = this.onetimePrice.max?.toString();
      } else {
        this.minPrice = undefined;
        this.maxPrice = undefined;
      }
      this.internalDiscounted = this.discounted;
    },
    onToggle(isOpened) {
      if (!isOpened) this.reset();
    },
    onClickaway() {
      this.reset();
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.price-filter-dropdown {
  &__input-item {
    padding: $grid-base;
    padding-top: 4px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    grid-gap: $grid-base;
    border-top: 1px solid var(--color-stroke-subtle);
    cursor: default;

    :deep(.input-wrapper) {
      flex: 1 1 100%;
    }
  }

  &__input-appendix {
    margin-bottom: $grid-base * 0.5;
  }
}
</style>
